import EventTypeCalendar from 'domains/models/event_type_calendar';
import { formatToISO } from 'infras/datetime';
import { z } from 'zod';
import {
  CalendarTypeEnum,
  FrequencyTypeEnum,
  PeriodTypeEnum,
  ViewerTimeZoneTypeEnum,
} from './enum';

export type EventTypeCalendarCreate = z.infer<
  typeof schemaEventTypeCalendarCreate
>;
export const schemaEventTypeCalendarCreate = z.object({
  startHour: z.number(),
  startMinute: z.number(),
  endHour: z.number(),
  endMinute: z.number(),
  startedAt: z.string(), // utc
  wday: z.number().nullable(),
  endedAt: z.string().nullable(), // utc
  frequencyType: FrequencyTypeEnum.nullable(),
  frequencyInterval: z.number().nullable(),
  schedulableType: z.string(),
  schedulableId: z.number().nullable(),
  exceptions: z.array(z.string()).nullable(),
  parentEventTypeCalendarId: z.number().nullable(),
});

export type EventTypeCalendarCreateBody = z.infer<
  typeof schemaEventTypeCalendarCreateBody
>;
export const schemaEventTypeCalendarCreateBody = z.object({
  calendars: z.array(schemaEventTypeCalendarCreate),
});

export type EventTypeCalendarUpdate = z.infer<
  typeof schemaEventTypeCalendarUpdate
>;
export const schemaEventTypeCalendarUpdate = z.object({
  periodType: PeriodTypeEnum.nullable(),
  periodDuration: z.number().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  bufferBefore: z.number().nullable(),
  bufferAfter: z.number().nullable(),
  timeZone: z.string(),
  isLimitGracePeriod: z.boolean(),
  gracePeriod: z.number().nullable(),
  gracePeriodPoint: z.number(),
  gracePeriodUnit: z.number(),
  calendarType: CalendarTypeEnum.optional(),
  eventTypeCalendarTemplateId: z.number().nullable().optional(),
  viewerTimeZoneType: ViewerTimeZoneTypeEnum,
});

export type EventTypeCalendarUpdateBody = z.infer<
  typeof schemaEventTypeCalendarUpdateBody
>;
export const schemaEventTypeCalendarUpdateBody = z.object({
  calendar: schemaEventTypeCalendarUpdate,
});

export type EventTypeCalendarItem = z.infer<typeof schemaEventTypeCalendarItem>;
export const schemaEventTypeCalendarItem = z.object({
  id: z.number(),
  startHour: z.number(),
  startMinute: z.number(),
  endHour: z.number(),
  endMinute: z.number(),
  startedAt: z.string(), // utc
  endedAt: z.string().nullable(), // utc
  wday: z.number(),
  frequencyType: FrequencyTypeEnum.nullable(),
  frequencyInterval: z.number().nullable(),
  exceptions: z.array(z.string()).nullable(),
  parentEventTypeCalendarId: z.number().nullable(),
});

export const toListItem = (calendar: EventTypeCalendar) => {
  return toItem(calendar);
};

export const toItem = (calendar: EventTypeCalendar) => {
  const startedAt = formatToISO(calendar.startedAt, 'utc');
  const endedAt = calendar.endedAt
    ? formatToISO(calendar.endedAt, 'utc')
    : null;
  let exceptions = [];
  try {
    exceptions = JSON.parse(calendar.exceptions);
  } catch (err) {
    console.log(err);
  }

  const item: EventTypeCalendarItem = {
    id: Number(calendar.id),
    startHour: calendar.startHour,
    startMinute: calendar.startMinute,
    endHour: calendar.endHour,
    endMinute: calendar.endMinute,
    startedAt,
    endedAt,
    wday: calendar.wday,
    frequencyType: calendar.frequencyType || null,
    frequencyInterval: calendar.frequencyInterval || null,
    exceptions,
    parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
  };
  return item;
};
